import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import * as d3 from 'd3';
import _ from 'lodash';
import './ConnectionsView.scss';
import { getDistances } from 'constants/matrices';
import classnames from 'classnames';
import Xarrow from 'react-xarrows';
import ButtonNew from 'components/inputs/ButtonNew';
import NodePill from 'components/NodePill';
import ConnectionsAdvanced from './ConnectionsAdvanced';
import { setNodeHover } from 'actions/viewStates';
import { useViewState } from '../../state/ViewState';

const ConnectionsPageHeaderText = ({
  showCascadeHighlight,
  showCascadeHasBeenClicked,
}) => {
  if (showCascadeHasBeenClicked) {
    // TODO wait for designs then implement
    return (
      <div className="intro-paragraph">
        The web of <span className="intro-paragraph-key-word">connections</span>{' '}
        can cause some unexpected downstream effects to any changes you make to
        a <span className="intro-paragraph-key-word">node</span>. Select an{' '}
        <span className="intro-paragraph-key-word">influencer</span> node to see
        its connections{' '}
        <span className="intro-paragraph-key-word">cascade</span>.
      </div>
    );
  } else if (showCascadeHighlight) {
    return (
      <div className="intro-paragraph">
        First order impacts are visualized here, the strength of the connection
        is represented by the line weight, the positive or negative value of the
        connection is shown by the color. For more information about an
        individual connection look in the results panel. Show cascade to see
        additional impacts.
      </div>
    );
  } else {
    return (
      <div className="intro-paragraph">
        The web of <span className="intro-paragraph-key-word">connections</span>{' '}
        can cause some unexpected downstream effects to any changes you make to
        a <span className="intro-paragraph-key-word">node</span>. Select an{' '}
        <span className="intro-paragraph-key-word">influencer</span> node to see
        its connections{' '}
        <span className="intro-paragraph-key-word">cascade</span>.
      </div>
    );
  }
};

// This one does have the modification sidebar
const ConnectionsPage = ({ matrix, viewMode }) => {
  const dispatch = useDispatch();

  const { showConnectionsTable, setShowConnectionsTable } = useViewState(
    state => state
  );

  let [mounted, setMounted] = useState(false);

  const connectionDistances = ['primary', 'secondary', 'tertiary'];

  let [connectionDistance, setConnectionDistance] = useState('primary');

  // Highlight cascade button
  let [showCascadeHighlight, setShowCascadeHighlight] = useState(false);
  let [showCascadeHasBeenClicked, setShowCascadeHasBeenClicked] = useState(
    false
  );

  // For animation ---------------------------------------------------------------
  const trophicCascadeLevels = [
    'primary',
    'secondary',
    'tertiary',
    'equilibrium',
  ];

  let [hovered, setHovered] = useState(null);

  let [selected, setSelected] = useState(null);
  let prevSelected = useRef(null);

  const [trophicLevel, setTrophicLevel] = useState(3);
  const cascadeInterval = useRef(null);

  const trophicCascadeLevel = useMemo(() => {
    return trophicCascadeLevels[trophicLevel].toLowerCase();
  }, [trophicLevel]);

  useEffect(() => {
    if (!!selected && showCascadeHasBeenClicked && showCascadeHighlight) {
      setHovered(null);
      setTrophicLevel(0);

      const interval = setInterval(() => {
        setTrophicLevel(prev => {
          if (prev >= 3) {
            clearInterval(cascadeInterval.current);
            cascadeInterval.current = null;
            return prev;
          }
          return prev + 1;
        });
      }, 2000);

      cascadeInterval.current = interval;
    }
  }, [showCascadeHasBeenClicked, selected, showCascadeHighlight]);

  const stopCascade = useCallback(() => {
    clearInterval(cascadeInterval.current);
    cascadeInterval.current = null;
    setTrophicLevel(3);
  });

  useEffect(() => {
    // If clicking primary connections, stop the cascade
    if (connectionDistance === 'primary' && cascadeInterval.current) {
      stopCascade();
    }
  }, [connectionDistance, stopCascade]);

  useEffect(() => {
    // If clicking a new node while one is selected
    if (
      !!selected &&
      !!prevSelected.current &&
      prevSelected.current !== selected
    ) {
      // Stop the cascade and start over
      if (connectionDistance !== 'primary') {
        stopCascade();
      }
      // Reset values
      prevSelected.current = selected;
      setConnectionDistance('primary');
      setShowCascadeHighlight(true);
      setShowCascadeHasBeenClicked(false);
    }
  }, [connectionDistance, selected, stopCascade]);

  useEffect(() => {
    if (
      !showCascadeHasBeenClicked &&
      !!selected &&
      connectionDistance === 'primary'
    ) {
      // If cascade hasn't run and we have selected a node
      setShowCascadeHighlight(true);
    }
    if (showCascadeHasBeenClicked && connectionDistance !== 'primary') {
      // If we have run the cascade for this node
      setShowCascadeHighlight(false);
    }
    if (!selected) {
      // If a node gets unselected
      setConnectionDistance('primary');
      setShowCascadeHighlight(false);
      setShowCascadeHasBeenClicked(false);
    }
  }, [showCascadeHasBeenClicked, selected, connectionDistance]);

  const availableConnectionDistances = useMemo(() => {
    const index =
      connectionDistances.findIndex(v => v === connectionDistance) + 1;
    return connectionDistances.slice(0, index);
  }, [connectionDistance]);

  const isShowingCascade = useMemo(() => {
    return !!availableConnectionDistances.includes('secondary');
  }, [availableConnectionDistances]);

  const nodes = useMemo(() => Object.values(matrix?.nodes), [matrix]);

  const getDistance = useCallback(
    (originNodeId, destinationNodeId) =>
      getDistances(matrix, originNodeId)[destinationNodeId],
    [matrix]
  );

  // TODO there is likely a memory leak in here
  const nodeLinks = useMemo(() => {
    const nodesByDistance = nodes.reduce((acc, n) => {
      for (const node of nodes) {
        const selfLoop = n.id === node.id && n.influences[n.id] !== undefined;

        acc.push({
          source: n.id,
          target: node.id,
          distance: selfLoop ? 1 : getDistance(n.id, node.id),
          connectionStrength: n?.influences?.[node.id] ?? null,
        });
      }

      return acc;
    }, []);

    let nextLinks = nodesByDistance.filter(n => n?.distance === 1);

    const primaryLinks = nextLinks.map(l => ({
      ...l,
      type: 'primary',
      highlight: selected === null ? true : selected === l.source,
    }));

    const secondaryLinks = nextLinks.reduce((acc, link) => {
      const { target } = link;
      const targetLinks = nextLinks
        .filter(l => l.source === target)
        .map(l => ({
          ...l,
          type: 'secondary',
          highlight:
            selected === null
              ? true
              : primaryLinks.some(
                  v =>
                    v.type === 'primary' && v.highlight && v.target === l.source
                ),
        }));
      acc = acc.concat(targetLinks);
      return acc;
    }, primaryLinks);

    const tertiaryLinks = nextLinks.reduce((acc, link) => {
      const { target } = link;
      const targetLinks = nextLinks
        .filter(l => l.source === target)
        .map(l => ({
          ...l,
          type: 'tertiary',
          highlight:
            selected === null
              ? true
              : secondaryLinks.some(
                  v =>
                    v.type === 'secondary' &&
                    v.highlight &&
                    v.target === l.source
                ),
        }));
      acc = acc.concat(targetLinks);
      return acc;
    }, secondaryLinks);

    switch (connectionDistance) {
      case 'primary': {
        nextLinks = primaryLinks;
        break;
      }
      case 'secondary': {
        nextLinks = secondaryLinks;
        break;
      }
      case 'tertiary': {
        nextLinks = tertiaryLinks;
        break;
      }
      default: {
        nextLinks = tertiaryLinks.map(l => ({ ...l, type: 'primary' }));
        break;
      }
    }

    nextLinks = _.uniqBy(
      nextLinks,
      obj =>
        `${obj.source}-${obj.target}-${obj.type}-${obj.distance}-${obj.connectionStrength}`
    );

    return nextLinks;
    // return nodesByDistance.filter(n => n?.distance !== Infinity);
  }, [connectionDistance, getDistance, nodes, selected]);

  const createDiagram = useCallback(() => {
    // Remove before redrawing
    d3.select('#my_dataviz')
      .selectAll('*')
      .remove();
  }, [nodeLinks, nodes, connectionDistance, selected]);

  useEffect(() => {
    if (!mounted) {
      createDiagram();
      setMounted(true);
    }
  }, []);

  useEffect(() => {
    if (connectionDistance) {
      createDiagram();
    }
  });

  useEffect(() => {
    if (hovered || selected) {
      createDiagram();
    }
  });

  const chordNodes = useMemo(
    () => nodes.map(n => ({ name: n.name, id: n.id, fixed: n.fixed })),
    [nodes]
  );

  const highlightedLinks = useMemo(() => {
    return nodeLinks.filter(l => l.highlight);
  }, [nodeLinks]);

  const getPrevSourceType = useCallback(type => {
    const index = connectionDistances.findIndex(v => v === type);
    if (index === 0) return 'base';
    return connectionDistances[index - 1];
  }, []);

  const getNextSourceType = useCallback(type => {
    const index = connectionDistances.findIndex(v => v === type);
    return connectionDistances[index + 1];
  }, []);

  const hoveredSourceAndTargets = useMemo(() => {
    if (!hovered || (!!selected && hovered.startsWith('base-'))) {
      return { sourceNodes: [], targetNodes: [] };
    }
    let [hoveredType, hoveredNodeId] = hovered.split('-');

    const prevSourceType = getPrevSourceType(hoveredType);
    const nextSourceType = getNextSourceType(hoveredType);

    let sourceNodes = highlightedLinks
      .filter(l => l.target === hoveredNodeId)
      .filter(l => l.type === hoveredType)
      .map(l => `${prevSourceType}-${l.source}`);

    let targetNodes = highlightedLinks
      .filter(l => l.source === hoveredNodeId)
      .filter(l => l.type === nextSourceType)
      .map(l => `${nextSourceType}-${l.target}`);

    // Should break this out into a function like below
    if (nextSourceType === 'secondary') {
      sourceNodes = sourceNodes.concat(targetNodes);

      // get tertiary
      let appended = highlightedLinks
        .filter(l => targetNodes.some(n => n === `${'secondary'}-${l.source}`))
        .filter(l => l.type === 'tertiary')
        .map(l => `${'tertiary'}-${l.target}`);

      targetNodes = targetNodes.concat(appended);
    }

    const getPrevAppended = (prevType, source) => {
      let appended = highlightedLinks
        .filter(l => source.some(n => n === `${prevType}-${l.target}`))
        .filter(l => l.type === prevType)
        .map(l => `${getPrevSourceType(prevType)}-${l.source}`);

      return [...new Set(appended)];
    };

    if (prevSourceType === 'primary') {
      targetNodes = targetNodes.concat(sourceNodes);

      // get primary
      sourceNodes = sourceNodes.concat(getPrevAppended('primary', sourceNodes));
    }

    if (prevSourceType === 'secondary') {
      targetNodes = targetNodes.concat(sourceNodes);

      // get primary and secondary
      let appendedTarget = getPrevAppended('secondary', sourceNodes);
      let appendedSource = getPrevAppended('primary', appendedTarget);

      targetNodes = targetNodes.concat(appendedTarget);
      sourceNodes = sourceNodes.concat(appendedSource).concat(appendedTarget);
    }

    sourceNodes = [...new Set(sourceNodes)];
    targetNodes = [...new Set(targetNodes)];

    return { sourceNodes, targetNodes };
  }, [
    hovered,
    selected,
    highlightedLinks,
    getNextSourceType,
    getPrevSourceType,
  ]);

  const getColor = useCallback(
    (value, start, end) => {
      let nextColor = value < 0 ? '#f46069' : '#4fb7cf';

      if (!hovered || (!!selected && hovered.startsWith('base-'))) {
        nextColor = `${nextColor}`;
        return nextColor;
      }

      const { sourceNodes, targetNodes } = hoveredSourceAndTargets;

      if (sourceNodes.includes(start) && targetNodes.includes(end)) {
        nextColor = `${nextColor}`;
      } else if (hovered === start || hovered === end) {
        nextColor = `${nextColor}`;
      } else {
        nextColor = `${nextColor}1A`;
      }

      return nextColor;
    },
    [hovered, selected, hoveredSourceAndTargets]
  );

  const highlightNodes = useMemo(() => {
    let next = highlightedLinks.filter(link => {
      const color = getColor(
        Number(link.connectionStrength),
        `${getPrevSourceType(link.type)}-${link.source}`,
        `${link.type}-${link.target}`,
        link.highlight
      );
      if (color.endsWith('1A')) return false;
      return true;
    });

    next = next
      .map(link => [
        `${getPrevSourceType(link.type)}-${link.source}`,
        `${link.type}-${link.target}`,
      ])
      .flat();

    return next;
  }, [highlightedLinks, getColor, getPrevSourceType]);

  const getIsLowOpacity = useCallback(
    elId => {
      return elId !== hovered && !highlightNodes.includes(elId);
    },
    [hovered, highlightNodes]
  );

  const getActualNode = useCallback(
    nodeId => {
      return nodes.find(n => n.id === nodeId);
    },
    [nodes]
  );

  const showLink = useCallback(
    (link, allLinks) => {
      if (trophicCascadeLevel === 'equilibrium') {
        return { show: true, delay: 0 };
      }

      const linksByType = allLinks.filter(l => l.type === trophicCascadeLevel);

      const usableIndex = linksByType.findIndex(l => _.isEqual(link, l));
      const staggerInterval = 1000 / linksByType.length;
      const result = usableIndex * staggerInterval;

      const linkIndex = trophicCascadeLevels.indexOf(link.type);
      const index = trophicCascadeLevels.indexOf(trophicCascadeLevel);
      if (index >= linkIndex) {
        return { show: true, delay: Math.max(result, 0) };
      }
      return { show: false };
    },
    [trophicCascadeLevel]
  );

  // Reset on leaving
  useEffect(() => {
    return () => setShowConnectionsTable(false);
  }, []);

  return (
    <div
      className={classnames('ConnectionsView', {
        padded: !showConnectionsTable,
      })}
    >
      {showConnectionsTable ? (
        <ConnectionsAdvanced
          matrix={matrix}
          onCancel={() => setShowConnectionsTable(false)}
        />
      ) : (
        <div className="ConnectionsView-graphic">
          <div className="ConnectionsView-top-bar">
            <ConnectionsPageHeaderText
              showCascadeHighlight={showCascadeHighlight}
              showCascadeHasBeenClicked={showCascadeHasBeenClicked}
            />

            <div className="ConnectionsView-buttons-container">
              {viewMode === 'edit' ? (
                <ButtonNew
                  buttonClasses={classnames('secondary-button-small', {
                    active: !isShowingCascade,
                  })}
                  label="Show table"
                  onClick={() => {
                    setShowConnectionsTable(true);
                  }}
                />
              ) : null}
              <ButtonNew
                buttonClasses={classnames('secondary-button-small', {
                  active: !isShowingCascade,
                })}
                label="Primary connections"
                onClick={() => {
                  setConnectionDistance('primary');
                }}
              />
              <ButtonNew
                buttonClasses={classnames(
                  'secondary-button-small tour-connections-cascade interactiveTourGettingStarted-5',
                  {
                    disabled: !selected,
                    active: isShowingCascade,
                    highlight: showCascadeHighlight,
                  }
                )}
                label="Show interactions"
                onClick={() => {
                  setConnectionDistance('tertiary');
                  setShowCascadeHasBeenClicked(true);
                }}
              />
            </div>
          </div>
          <div className="ConnectionsView-main">
            <div className="chart-container">
              <div className="divider-line"></div>

              <div className="column base">
                <div className="column-header">Influencer</div>
                <div className="column-main">
                  <div className="column-background"></div>
                  {chordNodes.map(node => (
                    <div
                      id={`base-${node.id}`}
                      className={classnames('node tour-connections-node', {
                        fixed: node.fixed,
                        unhovered: getIsLowOpacity(`base-${node.id}`),
                        active: selected === node.id,
                      })}
                      key={`base-${node.id}`}
                      onMouseEnter={() => {
                        if (!cascadeInterval.current) {
                          dispatch(setNodeHover(node.id));
                          setHovered(`base-${node.id}`);
                        }
                      }}
                      onMouseLeave={() => {
                        dispatch(setNodeHover());
                        setHovered(null);
                      }}
                      onClick={() =>
                        setSelected(prev => {
                          prevSelected.current = prev;
                          return selected === node.id ? null : node.id;
                        })
                      }
                    >
                      <NodePill
                        node={getActualNode(node.id)}
                        width={160}
                        backgroundColor={
                          selected === node.id ? '#303752' : null
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="column primary">
                <div className="column-header">
                  {!isShowingCascade ? 'Influenced' : 'Influencer/Influenced'}
                </div>
                <div className="column-main">
                  {chordNodes.map(node => (
                    <div
                      id={`primary-${node.id}`}
                      className={classnames('node', {
                        fixed: node.fixed,
                        unhovered: getIsLowOpacity(`primary-${node.id}`),
                      })}
                      key={`primary-${node.id}`}
                      onMouseEnter={() => {
                        if (!cascadeInterval.current) {
                          dispatch(setNodeHover(node.id));
                          setHovered(`primary-${node.id}`);
                        }
                      }}
                      onMouseLeave={() => {
                        dispatch(setNodeHover());
                        setHovered(null);
                      }}
                    >
                      <NodePill
                        node={getActualNode(node.id)}
                        width={isShowingCascade ? 80 : 160}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {isShowingCascade ? (
                <div className="column secondary">
                  <div className="column-header">Influencer/Influenced</div>
                  <div className="column-main">
                    {chordNodes.map(node => (
                      <div
                        id={`secondary-${node.id}`}
                        className={classnames('node', {
                          fixed: node.fixed,
                          unhovered: getIsLowOpacity(`secondary-${node.id}`),
                        })}
                        key={`secondary-${node.id}`}
                        onMouseEnter={() => {
                          if (!cascadeInterval.current) {
                            dispatch(setNodeHover(node.id));
                            setHovered(`secondary-${node.id}`);
                          }
                        }}
                        onMouseLeave={() => {
                          dispatch(setNodeHover());
                          setHovered(null);
                        }}
                      >
                        <NodePill
                          node={getActualNode(node.id)}
                          width={isShowingCascade ? 80 : 160}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {isShowingCascade ? (
                <div className="column tertiary">
                  <div className="column-header">Influenced</div>
                  <div className="column-main">
                    {chordNodes.map(node => (
                      <div
                        id={`tertiary-${node.id}`}
                        className={classnames('node', {
                          fixed: node.fixed,
                          unhovered: getIsLowOpacity(`tertiary-${node.id}`),
                        })}
                        key={`tertiary-${node.id}`}
                        onMouseEnter={() => {
                          if (!cascadeInterval.current) {
                            dispatch(setNodeHover(node.id));
                            setHovered(`tertiary-${node.id}`);
                          }
                        }}
                        onMouseLeave={() => {
                          dispatch(setNodeHover());
                          setHovered(null);
                        }}
                      >
                        <NodePill node={getActualNode(node.id)} width={160} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div
              key={highlightedLinks
                .map(
                  link =>
                    `source-${getPrevSourceType(link.type)}-${
                      link.source
                    }-target-${link.type}-${link.target}`
                )
                .concat([`${hovered === null ? '' : `hover:${hovered}`}`])
                .join('--')}
            >
              {highlightedLinks.map((link, i) =>
                showLink(link, highlightedLinks)?.show ? (
                  <Xarrow
                    key={`source-${getPrevSourceType(link.type)}-${
                      link.source
                    }-target-${link.type}-${link.target}`}
                    start={`${getPrevSourceType(link.type)}-${link.source}`}
                    end={`${link.type}-${link.target}`}
                    strokeWidth={1}
                    headSize={5}
                    tailSize={5}
                    color={getColor(
                      Number(link.connectionStrength),
                      `${getPrevSourceType(link.type)}-${link.source}`,
                      `${link.type}-${link.target}`,
                      link.highlight
                    )}
                    endAnchor={'left'}
                    startAnchor={'right'}
                    SVGcanvasProps={
                      trophicCascadeLevel !== 'equilibrium'
                        ? {
                            className: 'active-arrow',
                          }
                        : null
                    }
                    arrowBodyProps={
                      trophicCascadeLevel !== 'equilibrium'
                        ? {
                            pathLength: 1,
                            style: {
                              animationDelay: `${
                                showLink(link, highlightedLinks)?.delay
                              }ms`,
                            },
                          }
                        : null
                    }
                    arrowHeadProps={
                      trophicCascadeLevel !== 'equilibrium'
                        ? {
                            className: 'active-arrow-head',
                            style: {
                              animation: `appear ${1000 +
                                showLink(link, highlightedLinks)
                                  ?.delay}ms step-end forwards`,
                            },
                          }
                        : null
                    }
                  />
                ) : null
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsPage;
